import { Component } from '@angular/core';
import { amorphicStatic } from '@haventech/amorphic';
import { POLICY_DOC_ANNUITY_DOCTYPE } from '../../../../../../common/js/constants/policy-doc';
import { productTypes } from '../../../../../../common/js/static/Product';
import { BasePolicyBannerComponent } from '../../../../../../coverpath-common/angular/components/account-center/policy-banner/base-policy-banner.component';
import { ToastService } from '../../../../../../coverpath-common/angular/components/overlays/toaster/toast-service.service';
import { AccountCenterService } from '../../../../../../coverpath-common/angular/services/account-center/account-center.service';
import { AmorphicService } from '../../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { ModalService } from '../../../../../../coverpath-common/angular/services/modal/modal.service';
import type { CoverpathAnnuity } from '../../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuity';
import type { CoverpathPolicy } from '../../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { ModalType } from '../../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { AccountCenterController } from '../../../amorphicControllers/AccountCenterController';
import { CoverpathAnnuityStatus } from '../policy-status/annuity-status';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../../../../common/js/logger/UILoggerSingleton';

@Component({
  selector: 'annuity-banner',
  moduleId: module.id,
  templateUrl: './annuity-banner.component.html',
})
export class AnnuityBannerComponent extends BasePolicyBannerComponent {
  async ngOnInit(): Promise<void> {
    this.subscribeToAccountCenterService();
    this.isLoading = false;
    await this.setAllProperties();
    this.isLoading = false;
  }

  constructor(
    amorphicService: AmorphicService,
    accountCenterService: AccountCenterService,
    private modalService: ModalService,
    private toastService: ToastService
  ) {
    super(amorphicService, accountCenterService, modalService, toastService);
    this.policyStatus = new CoverpathAnnuityStatus(
      this.amorphicService,
      this.amorphicService.controller.customer.application.primaryPolicy as CoverpathAnnuity
    );
  }

  async setAllProperties(): Promise<void> {
    await super.setAllProperties();
  }

  setupButtonActions() {
    if (this.action === 'Customer Confirm Application:Waiting for customer signature') {
      this.buttonText = `Continue to eSign`;
      this.clickAction = this.eSignAnnuity;
      this.loadingEsign = false;
    } else if (this.action === `Application:Waiting for Annuity Acceptance`) {
      this.buttonText = `Accept Offer`;
      this.clickAction = this.acceptAnnuity;
      this.loadingEsign = false;
    } else {
      this.buttonText = null;
    }
  }

  async eSignAnnuity() {
    this.loadingEsign = true;
    const formsToSign = await this.setupDocumentsToBeSigned();
    this.loadingEsign = false;
    const modalConfig = {
      type: ModalType.esignModal,
      formsToSign,
      onFinishSigning: async () => {
        this.loadingEsign = true;
        await (this.amorphicService.controller.accountCenterController as AccountCenterController).esignAnnuity();
      },
    };

    await this.modalService.activate(modalConfig);
  }

  async acceptAnnuity() {
    const annuity = this.policy as CoverpathAnnuity;
    const modalConfig = {
      type: ModalType.paymentModal,
      totalCoverage: annuity.initialPurchaseAmount,
      productDescription: productTypes.Annuity.productNames[annuity.product],
      amountDue: annuity.initialPurchaseAmount,
      coverageLabel: annuity.productType.toUpperCase(),
      onSubmit: async () => {
        this.loadingEsign = true;
        await (this.amorphicService.controller.accountCenterController as AccountCenterController).acceptAnnuityOffer();
      },
    };

    await this.modalService.activate(modalConfig);
  }

  async setupDocumentsToBeSigned() {
    const docInfos = await this.amorphicService.controller.accountCenterController.getAnnuityAppDocuments();

    const formsToSign = docInfos.map((docInfo) => {
      return {
        formType: docInfo.name,
        pdfUrl: this.amorphicService.controller.accountCenterController.getViewAnnuityDocURL(docInfo.type),
        textPrompt: ``,
        eSignLabel: `Sign ${docInfo.displayName}`,
        signFn: async () => {
          if (docInfo.requiresOwnerSignature) {
            await this.amorphicService.controller.accountCenterController.signAnnuityForm(
              this.policy as CoverpathPolicy,
              docInfo.type,
              this.policy.getOwnerSigningName(),
              undefined
            );
            UILoggerSingleton.getInstance().info(
              {
                module: 'AnnuityBanner',
                function: 'signFn',
                category: UICategory.milestone,
                context: {
                  piiLevel: UIPiiLevelEnum.nonPII,
                },
              },
              `${docInfo.displayName} form signed`
            );
          } else {
            UILoggerSingleton.getInstance().info(
              {
                module: 'AnnuityBanner',
                function: 'signFn',
                category: UICategory.milestone,
                context: {
                  piiLevel: UIPiiLevelEnum.nonPII,
                },
              },
              `${docInfo.displayName} form doesn't require client signature`
            );
          }
        },
      };
    });

    return formsToSign;
  }
}
